/**
 * Onboarding step collecting the organization's country, and its company ID when
 * that country has a business register we can pre-fill from.
 */
import { useCallback, useEffect } from 'react';
import { Button, CountrySelector, Icon, InputWrapper, Loader, Modal } from '@insio/ui-toolkit';
import { CompanyRegistryLookupRow } from '@/components/CompanyRegistryLookupRow';
import { useOrganizationStep } from './use-organization-step';

interface OrganizationStepProps {
  /** Advances to the next onboarding step. */
  onDone: () => void;
  /** Leaves onboarding entirely. */
  onSkip: () => void;
}

export function OrganizationStep({ onDone, onSkip }: OrganizationStepProps) {
  const {
    t,
    phase,
    country,
    changeCountry,
    countryError,
    savingCountry,
    saveCountry,
    registryCountry,
    registryLoading,
    registryError,
    registryApplied,
    applyRegistryLookup,
    clearRegistryFeedback
  } = useOrganizationStep();

  // Nothing to collect without `account.settings` or without a party — move straight on.
  useEffect(() => {
    if (phase === 'skip') onDone();
  }, [phase, onDone]);

  /** Saves the country, optionally fills from the register, then advances. */
  const advance = useCallback(
    async (companyId: string | null) => {
      if (!(await saveCountry())) return;
      if (companyId && !(await applyRegistryLookup(companyId))) return;
      onDone();
    },
    [saveCountry, applyRegistryLookup, onDone]
  );

  if (phase !== 'ready') {
    return (
      <>
        <Modal.Header onClose={onSkip}>
          <span className='text-lg font-medium'>{t('header')}</span>
        </Modal.Header>

        <Modal.Content className='flex items-center justify-center p-xl'>
          <Loader size='sm' />
        </Modal.Content>
      </>
    );
  }

  const busy = savingCountry || registryLoading;

  return (
    <>
      <Modal.Header onClose={onSkip}>
        <span className='text-lg font-medium'>{t('header')}</span>
      </Modal.Header>

      <Modal.Content className='flex flex-col gap-lg p-lg sm:p-xl'>
        <p className='text-sm text-foreground-muted'>{t('subtitle')}</p>

        <InputWrapper
          label={t('countryLabel')}
          htmlFor='onboarding-country'
          required
          isError={!!countryError}
          errorText={countryError ?? undefined}
        >
          <CountrySelector
            id='onboarding-country'
            testId='onboarding-country'
            value={country}
            onChange={changeCountry}
            isSearchable
            height='md'
            hasError={!!countryError}
            unselectedOptionLabel={t('countryPlaceholder')}
          />
        </InputWrapper>

        {registryCountry && (
          <CompanyRegistryLookupRow
            id='onboarding-company-id'
            label={t(`registry.${registryCountry}.label`)}
            placeholder={t(`registry.${registryCountry}.placeholder`)}
            hint={t(`registry.${registryCountry}.hint`)}
            loading={registryLoading}
            error={registryError}
            applied={registryApplied}
            disabled={busy}
            onApply={companyId => void advance(companyId)}
            onQueryChange={clearRegistryFeedback}
          />
        )}

        <div className='flex gap-sm rounded-lg bg-info-bg px-md py-sm'>
          <Icon name='circle-info' size='sm' className='text-info-text mt-2xs shrink-0' />
          <p className='text-sm text-info-text'>{t('locationHint')}</p>
        </div>
      </Modal.Content>

      <Modal.Footer className='flex-col! items-stretch!'>
        <Modal.Action>
          <Button
            variant='primary'
            size='md'
            onClick={() => void advance(null)}
            disabled={busy || !country}
            className='w-full bg-black! hover:bg-foreground-base! text-white!'
          >
            {registryCountry ? t('skipLookup') : t('continue')}
          </Button>
        </Modal.Action>
      </Modal.Footer>
    </>
  );
}
